import {Table} from 'notebook/tables/constants/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * Computes the column index of the first non-single-input column.
 * Returns null if there are no single input columns (boundary index is 0).
 */
export function computeSingleInputBoundaryIndex(table: Table): number | null {
  const index = TableUtil.findSingleInputBoundaryIndex(table);
  return index === 0 ? null : index;
}

/**
 * The pieces of the table that affect the slicing of the displayed table. This is important for
 * Single Inputs which offset the table indices.
 *
 * start and end are INCLUSIVE when specified.
 */
export function computeSingleInputSlices(boundaryIndex: number | null): {
  start: number | null;
  end: number | null;
} {
  return {
    start: boundaryIndex || null,
    end: null,
  };
}

/**
 * The start and end of the table slice, defaulting them to 0 and the last column index
 * respectively if the table is not actually sliced.
 *
 * @param slices - The raw slices from {@link computeSingleInputSlices}.
 * @param lastColumnIndex - The last column index of the table (`table.table[0].length - 1`).
 */
export function computeEffectiveSingleInputSlices(
  slices: {start: number | null; end: number | null},
  lastColumnIndex: number
): {start: number; end: number} {
  return {
    start: slices.start ?? 0,
    end: slices.end ?? lastColumnIndex,
  };
}

/**
 * The pixel width of the sliced table (from the single input boundary to the end of the table).
 */
export function computeSingleInputSliceWidthPx(
  table: Table,
  shouldShowGhostCells: boolean,
  sliceStart: number | null
): number {
  return TableUtil.tableWidth(table.widths, shouldShowGhostCells, {
    sliceStart,
    sliceEnd: null,
    firstColumnWidth: table._firstColumnWidth,
  });
}

/**
 * Whether the single input group should be visible.
 */
export function computeIsSingleInputVisible(isCollapsed: boolean, boundaryIndex: number | null): boolean {
  return !isCollapsed && Boolean(boundaryIndex);
}
